import { FacturationCode } from "../../Models/MedfarBilling/FacturationCode";

class FacturationCodeHelper {
    public codeGroups: { [key: string]: FacturationCode };

    constructor(initCodeGroup: boolean = false) {
        this.codeGroups = {};

        if (initCodeGroup)
            this.initFacturationCodeGroups();
    }

    public requiresReferringDoctor(codeGroupId: string): boolean {
        return codeGroupId === "GROSSESSE--PECavecref" || codeGroupId === "EvalRef--ProbMineur" || codeGroupId === "EvalRef--ProbMajeur";
    }

    public requiresSpecialistCommunication(codeGroupId: string): boolean {
        return codeGroupId === "Communications--Specialiste";
    }

    public requiresProfessionalCommunication(codeGroupId: string): boolean {
        return codeGroupId === "Communications--Profressionnel";
    }

    public isIntervention(codeGroupId: string): boolean {
        return codeGroupId === "AUTRES1--InterventionClinique" || codeGroupId === "AUTRES1--InterventionClinique_supp";
    }

    public isInterventionSupp(codeGroupId: string): boolean {
        return codeGroupId === "AUTRES1--InterventionClinique_supp";
    }

    public requiresDateOfBirth(codeGroupId: string): boolean {
        return codeGroupId === "GROSSESSE--PECsansref" || codeGroupId === "GROSSESSE--PECavecref" || codeGroupId === "GROSSESSE--PEC1eTrim";
    }

    public isCnesstCode(id: string | number): boolean {
        // code group id
        if (typeof id == "string" && id.indexOf("CSST--") !== -1)
            return true;

        // code
        const code = typeof id == "number" ? id : parseInt(id);
        return [15795, 15796, 15797].some(p => p === code);
    }

    private addCode(code: number, identifier: string, description: string, options?: { moreThen500PatientModifier?: number, is80YearsOldModifier?: number, isVulnerableModifier?: number, unitLabel1?: string, unitType1?: string, unitLabel2?: string, unitType2?: string, additionalCodeSuggestions?: string[], moreThen1000PatientModifier?: number, moreThen1500PatientModifier?: number, mustHaveAtLeast500Patient?: boolean }): this {
        options = options || {};

        this.codeGroups[identifier] = new FacturationCode({
            CodeGroupIdentifier: identifier,
            BaseCode: code,
            Description: description,
            AdditionalCodeSuggestions: options.additionalCodeSuggestions,
            UnitLabel1: options.unitLabel1,
            UnitType1: options.unitType1,
            UnitLabel2: options.unitLabel2,
            UnitType2: options.unitType2,
            Is80YearsOldModifier: options.is80YearsOldModifier || 0,
            IsVulnerableModifier: options.isVulnerableModifier || 0,
            MoreThen500PatientModifier: options.moreThen500PatientModifier || 0,
            MoreThen1000PatientModifier: options.moreThen1000PatientModifier || 0,
            MoreThen1500PatientModifier: options.moreThen1500PatientModifier || 0,
            MustHaveAtLeast500Patient: options.mustHaveAtLeast500Patient || false
        });

        return this;
    }

    private initFacturationCodeGroups(): void {
        this
            .addCode(15801, "RDVPI--PEC", "Prise en charge", { moreThen500PatientModifier: 1, is80YearsOldModifier: 14, isVulnerableModifier: 20, additionalCodeSuggestions: ["19957", "19958", "19959", "19960"] })
            .addCode(15803, "RDVPI--Suivi", "Suivi", { moreThen500PatientModifier: 1, is80YearsOldModifier: 14, isVulnerableModifier: 20 })
            .addCode(15819, "RDVPI--PERVUL", "Périodique (pt. Vulnérable)", { moreThen500PatientModifier: 1, is80YearsOldModifier: 20, isVulnerableModifier: 0 })
            .addCode(15813, "RDVPI--PERPED", "Périodique pédiatrique", { moreThen500PatientModifier: 1, is80YearsOldModifier: 0, isVulnerableModifier: 20 })

            .addCode(15805, "GROSSESSE--PECsansref", "PEC grossesse 1e trim. sans ref.", { moreThen500PatientModifier: 1, is80YearsOldModifier: 0, isVulnerableModifier: 20 })
            .addCode(15807, "GROSSESSE--PECavecref", "PEC grossesse 1e trim. avec ref.", { moreThen500PatientModifier: 1, is80YearsOldModifier: 0, isVulnerableModifier: 20 })
            .addCode(15809, "GROSSESSE--PEC1eTrim", "PEC grossesse > 1e trim.", { moreThen500PatientModifier: 1, is80YearsOldModifier: 0, isVulnerableModifier: 20 })
            .addCode(15811, "GROSSESSE--SuiviGrossesse", "Suivi grossesse", { moreThen500PatientModifier: 1, is80YearsOldModifier: 0, isVulnerableModifier: 20 })

            .addCode(15781, "DOMICILE--VD1", "Premier patient", { moreThen500PatientModifier: 0, is80YearsOldModifier: 0, isVulnerableModifier: 2 })
            .addCode(15782, "DOMICILE--VD2", "Patient additionnel", { moreThen500PatientModifier: 0, is80YearsOldModifier: 0, isVulnerableModifier: 2 })

            .addCode(15765, "SRVPATNONINSCRIT--VPM", "Visite ponctuelle mineur", { moreThen500PatientModifier: 1, is80YearsOldModifier: 4, isVulnerableModifier: 2 })
            .addCode(15773, "SRVPATNONINSCRIT--VPC", "Visite ponctuelle complexe", { moreThen500PatientModifier: 1, is80YearsOldModifier: 4, isVulnerableModifier: 2 })

            .addCode(15785, "AUTRES1--InterventionPsy", "Psycho")
            .addCode(15786, "AUTRES1--InterventionPsy_supp", "Psych Th. Supp.", { unitLabel1: "Temp Supp.", unitType1: "min" })
            .addCode(8857, "AUTRES1--InterventionClinique", "Int.Clin.")
            .addCode(8859, "AUTRES1--InterventionClinique_supp", "Int. Clin. Supp.", { unitLabel1: "Temp Supp.", unitType1: "min" })

            .addCode(15230, "AUTRES2--ITSS", "ITSS")
            .addCode(15188, "AUTRES2--Interprete", "Interprète")
            .addCode(9313, "AUTRES2--EnsGly", "Enseignement Glycé")
            .addCode(8877, "AUTRES2--ABCDaire", "ABCdaire")
            .addCode(15144, "AUTRES2--SupptempGrossesse", "Sup suivi de grossesse") // Examen (Patient de moins de 70 ans)  / en cabinet / supplément  de responsabilité pour suivi de grossesse (Voir le paragraphe 7.02 B) de l'E.P.  Médecine de famille,  prise en charge et suivi de la clientèle)
            .addCode(15189, "AUTRES2--SuppPECtempGrossesse", "Sup PEC tmp gross") // Pas de MD famille dans le GMF
            .addCode(15159, "AUTRES2--SuppPECtempGrossesse1erTimestre", "Sup PEC gross. 1er Trim")
            .addCode(19074, "AUTRES2--SuppPECtempGrossesseTransfert", "Sup PEC tmp gross. GMF") // MD famille dans GMF
            .addCode(15169, "AUTRES2--SuppForfaitrespvul", "Forfait responsabilité vulnérable")
            .addCode(15171, "AUTRES2--SuppForfaitrespvulInscrit", "Forfait responsabilité vulnérable (domicile)")

            .addCode(8875, "AUTRES2--ForfaitGMFAnnuel", "Forfait GMF ANNUEL") // 1 fois pr année, premiere visite anné civile, lors de la premiere visite

            .addCode(15789, "EvalRef--ProbMineur", "Problème mineur référé")
            .addCode(15790, "EvalRef--ProbMajeur", "Problème complexe référé")

            .addCode(9926, "CSST--9926", "9926 Initial")
            .addCode(9927, "CSST--9927", "9927 PEC")
            .addCode(9928, "CSST--9928", "9928 Évolution")
            .addCode(9930, "CSST--9930", "9930 Final")
            .addCode(9971, "CSST--9971", "9971 Assign. Temp.")
            .addCode(9970, "CSST--9970", "9970 Retrait préventif")
            // .addCode(9900, "CSST--9900", "9900 Avis motivé")
            .addCode(9929, "CSST--9929", "9929 Info med. complémentaire écrite")
            .addCode(9909, "CSST--9909", "9909 Rapport Complémentaire")
            .addCode(9907, "CSST--9907", "9907 Bilan téléphonique")
            .addCode(9944, "CSST--9944", "9944 Rapport d'évaluation médicale")
            .addCode(9916, "CSST--9916", "9916 Considération de complexité")

            // Forfaits de prise en charge.
            .addCode(15841, "Communications--Specialiste", "Comm. Spécialiste", { moreThen1000PatientModifier: 1, moreThen1500PatientModifier: 2 })
            .addCode(15844, "Communications--Profressionnel", "Comm. autre Professionnel", { moreThen1000PatientModifier: 1, moreThen1500PatientModifier: 2 });
    }
}

export { FacturationCodeHelper };
